import React, { useState, useEffect } from "react";
import {
    Modal,
    Button,
    FormGroup,
    ControlLabel,
    FormControl,
    Row,
    Col,
    Alert,
    Glyphicon
} from "react-bootstrap";
import { updateOrder, getStripeCustomerSubscriptions, getStripeCustomer, getUserByEmail, getUsersByBuilder } from "../utils/bedrockAPI";
import { CopyToClipboard } from 'react-copy-to-clipboard';

export default function OrderEditModal({ show, onHide, order, onUpdate, setOrder }) {
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [subscriptions, setSubscriptions] = useState([]);
    const [stripeCustomer, setStripeCustomer] = useState(null);
    const [suggestions, setSuggestions] = useState({});
    const [loadingData, setLoadingData] = useState(false);
    const [loadedOrderId, setLoadedOrderId] = useState(null);
    const [builderUsers, setBuilderUsers] = useState([]);
    const [rateWarning, setRateWarning] = useState(false);

    const orderStatuses = [
        'PENDING',
        'SUBMITTED', 
        'SHIPPING',
        'SHIPPED',
        'ARRIVED',
        'INSTALLED',
        'PROMOTIONAL',
        'RETURNING',
        'RETURNED',
        'REPLACEMENT',
        'DELETE'
    ];

    // Reset data when modal is hidden
    useEffect(() => {
        if (!show) {
            setBuilderUsers([]);
            setSubscriptions([]);
            setStripeCustomer(null);
            setSuggestions({});
            setLoadedOrderId(null);
            setLoadingData(false);
        }
    }, [show]);

    useEffect(() => {
        if (order && order.orderId !== loadedOrderId) {
            // Clear previous data first
            setBuilderUsers([]);
            setSubscriptions([]);
            setStripeCustomer(null);
            setSuggestions({});
            
            setFormData({
                status: order.status || 'PENDING',
                deliveryDate: order.deliveryDate ? new Date(order.deliveryDate).toISOString().split('T')[0] : '',
                notes: order.notes || '',
                subscriptionId: order.subscriptionId || '',
                purchaserId: order.purchaserId || '',
                rate: order.rate || '',
                poNumber: order.poNumber || ''
            });
            
            // Load additional data when modal opens
            loadModalData();
            setLoadedOrderId(order.orderId);
        }
    }, [order, loadedOrderId]);

    // Auto-select purchaser when subscription is assigned
    useEffect(() => {
        if (formData.subscriptionId && suggestions.purchaserId && !formData.purchaserId) {
            setFormData(prev => ({
                ...prev,
                purchaserId: suggestions.purchaserId
            }));
        }
    }, [formData.subscriptionId, suggestions.purchaserId, formData.purchaserId]);

    // Auto-populate rate when subscription is assigned
    useEffect(() => {
        if (formData.subscriptionId && order) {
            const selectedSubscription = subscriptions.find(sub => sub.id === formData.subscriptionId);
            if (selectedSubscription) {
                const subscriptionRate = selectedSubscription.items?.data?.[0]?.price?.unit_amount/100 || 0;
                
                // Only auto-populate if rate is empty or different
                if (!formData.rate || formData.rate !== subscriptionRate) {
                    setFormData(prev => ({
                        ...prev,
                        rate: subscriptionRate
                    }));
                    
                    // Show warning if there's an existing rate that's different
                    if (order.rate && order.rate !== subscriptionRate) {
                        setRateWarning(true);
                    } else {
                        setRateWarning(false);
                    }
                }
            }
        }
    }, [formData.subscriptionId, subscriptions, order?.rate]);

    const loadModalData = async () => {
        if (!order?.builderId) return;
        
        setLoadingData(true);
        setSuggestions({});

        try {
            // Load builder users
            const usersData = await getUsersByBuilder(order.builderId);
            if (usersData) {
                if(order.builderId === 'a45efc4c-9fb9-479b-9370-f80a99bcafde'){
                    console.log('usersData:', usersData);
                }
                setBuilderUsers(usersData);
            }
        } catch (err) {
            console.error('Failed to load builder users:', err);
        }

        try {
            // Load subscriptions
            const subsData = await getStripeCustomerSubscriptions(order.builderId);
            if (subsData && subsData.subscriptions) {
                setSubscriptions(subsData.subscriptions);
            }
            
            // Load Stripe customer
            const customerData = await getStripeCustomer(order.builderId);
            setStripeCustomer(customerData);
            
            // Check if billing email exists as user
            if (customerData?.email) {
                try {
                    const userExists = await getUserByEmail(customerData.email);
                    if (userExists && userExists.length > 0 && userExists[0].userId) {
                        const suggestedPurchaserId = userExists[0].userId;
                        
                        // Only suggest if the current purchaser is different or not set
                        if (!order.purchaserId || order.purchaserId !== suggestedPurchaserId) {
                            setSuggestions(prev => ({
                                ...prev,
                                purchaserId: suggestedPurchaserId,
                                purchaserEmail: customerData.email
                            }));
                        }
                    }
                } catch (err) {
                    console.warn('Failed to check user existence:', err);
                }
            }
            
            // Check if delivery date should be suggested from Stripe customer metadata
            if (!order.deliveryDate && customerData?.metadata?.delivery_date) {
                try {
                    const stripeDeliveryDate = new Date(customerData.metadata.delivery_date);
                    if (!isNaN(stripeDeliveryDate.getTime())) {
                        const formattedDate = stripeDeliveryDate.toISOString().split('T')[0];
                        setSuggestions(prev => ({
                            ...prev,
                            suggestedDeliveryDate: formattedDate,
                            stripeDeliveryDate: customerData.metadata.delivery_date
                        }));
                    }
                } catch (err) {
                    console.warn('Failed to parse delivery date from Stripe metadata:', err);
                }
            }
        } catch (err) {
            console.error('Failed to load modal data:', err);
        } finally {
            setLoadingData(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const next = { ...prev, [field]: value };
            // Clearing subscription: zero rate only when user switches from a subscription to none.
            // If the order already had no subscription, do not force rate to 0 when they edit rate.
            if (field === 'subscriptionId' && !value && prev.subscriptionId) {
                next.rate = 0;
                setRateWarning(false);
            }
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // Prepare update data
            const updateData = {};
            
            if (formData.status !== order.status) {
                updateData.status = formData.status;
            }
            
            if (formData.deliveryDate !== (order.deliveryDate ? new Date(order.deliveryDate).toISOString().split('T')[0] : '')) {
                updateData.deliveryDate = formData.deliveryDate + 'T00:00:00Z';
            }
            
            if (formData.notes !== order.notes && formData.notes !== '') {
                updateData.notes = formData.notes;
            }
            
            // Handle subscription assignment
            if (formData.subscriptionId !== order.subscriptionId) {
                // If subscriptionId is empty string, set it to null for database
                updateData.subscriptionId = formData.subscriptionId || null;
                
                if (formData.subscriptionId) {
                    // Find the selected subscription to get its created date
                    const selectedSubscription = subscriptions.find(sub => sub.id === formData.subscriptionId);
                    if (selectedSubscription?.created) {
                        // Convert unix timestamp to UTC timestamp for loopback
                        const createdDate = new Date(selectedSubscription.created * 1000);
                        updateData.addDate = createdDate.toISOString();
                    }
                } else {
                    // If removing subscription, clear the addDate to use the original order date
                    updateData.addDate = order.addDate;
                }
            }
            
            // Handle purchaser assignment
            if (formData.purchaserId && formData.purchaserId !== order.purchaserId) {
                updateData.purchaserId = formData.purchaserId;
            }
            
            // Handle rate assignment
            if (formData.rate !== order.rate) {
                updateData.rate = parseInt(formData.rate) || 0;
            }
            
            // Handle PO number assignment
            if (formData.poNumber !== order.poNumber) {
                updateData.poNumber = formData.poNumber || null;
            }
            
            // Handle suggested delivery date from Stripe metadata
            if (suggestions.suggestedDeliveryDate && suggestions.suggestedDeliveryDate !== formData.deliveryDate) {
                updateData.deliveryDate = suggestions.suggestedDeliveryDate + 'T00:00:00Z';
            }
            
            // Update order if there are changes
            if (Object.keys(updateData).length > 0) {
                // console.log('Updating order with:', updateData);
                await updateOrder(order.orderId, updateData);
            }

            setSuccess(true);
            
            // Create updated order object to pass back
            const updatedOrder = {
                ...order,
                ...updateData
            };
            
            // console.log('Modal creating updatedOrder:', updatedOrder);
            // console.log('updateData:', updateData);
            
            if (onUpdate) {
                onUpdate(updatedOrder);
            }
            
            // Close modal after a short delay
            setTimeout(() => {
                onHide();
                setSuccess(false);
            }, 1500);

        } catch (err) {
            setError('Failed to update order. Please try again.');
            console.error('Error updating order:', err);
        } finally {
            setLoading(false);
        }
    };

    const getSubscriptionInfo = (sub) => {
        // First try to get info from the assigned subscription
        if (sub) {
            const quantity = sub.quantity || 0;
            const monthlyAmount = sub.items?.data?.[0]?.price?.unit_amount || 0;
            const startDate = sub.created ? new Date(sub.created * 1000).toLocaleDateString() : 'N/A';
            return `${quantity} cams - $${Math.round(monthlyAmount / 100)}/month - Started ${startDate} - ${sub.description}`;
        }

        return order.cap?.name || 'No Cap';
    };

    const handleClose = () => {
        setFormData({});
        setError(null);
        setSuccess(false);
        setSuggestions({});
        setSubscriptions([]);
        setStripeCustomer(null);
        setBuilderUsers([]);
        setLoadedOrderId(null);
        setRateWarning(false);
        setLoadingData(false);
        onHide();
    };

    if (!order) return null;

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontFamily: 'inherit', fontSize: '18px', fontWeight: 'normal' }}>
                    <Glyphicon glyph="edit" style={{marginRight: "8px"}} />
                    {order.builder?.name} - {getSubscriptionInfo(order.subscription)}
                </Modal.Title>
            </Modal.Header>
            
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert bsStyle="danger">
                            <Glyphicon glyph="exclamation-sign" /> {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert bsStyle="success">
                            <Glyphicon glyph="ok" /> Order updated successfully!
                        </Alert>
                    )}

                    {loadingData && (
                        <Alert bsStyle="info">
                            <Glyphicon glyph="refresh" className="spinning" /> Loading order data...
                        </Alert>
                    )}

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <ControlLabel>Order Status</ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    value={formData.status}
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                >
                                    {orderStatuses.map(status => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </FormControl>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <ControlLabel>Delivery Date</ControlLabel>
                                <FormControl
                                    type="date"
                                    value={formData.deliveryDate}
                                    onChange={(e) => handleInputChange('deliveryDate', e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <ControlLabel>Tracking Number</ControlLabel>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <FormControl
                                        type="text"
                                        value={order.cap?.tracking || 'N/A'}
                                        disabled
                                        style={{ flex: 1, marginRight: '5px' }}
                                    />
                                    <CopyToClipboard text={order.cap?.tracking || ''}>
                                        <Button bsSize="xs">
                                            <Glyphicon glyph="copy" />
                                        </Button>
                                    </CopyToClipboard>
                                </div>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <ControlLabel>Subscription</ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    value={formData.subscriptionId}
                                    onChange={(e) => handleInputChange('subscriptionId', e.target.value)}
                                >
                                    <option value="">No Subscription</option>
                                    {subscriptions.map(sub => (
                                        <option key={sub.id} value={sub.id}>
                                            {getSubscriptionInfo(sub)}
                                        </option>
                                    ))}
                                </FormControl>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <ControlLabel>Purchaser</ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    value={formData.purchaserId}
                                    onChange={(e) => handleInputChange('purchaserId', e.target.value)}
                                >
                                    <option value="">Select Purchaser</option>
                                    {builderUsers.map(user => (
                                        <option key={user.userId} value={user.userId}>
                                            {user.firstName} {user.lastName} ({user.email})
                                        </option>
                                    ))}
                                </FormControl>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <ControlLabel>Monthly Rate</ControlLabel>
                                <FormControl
                                    type="number"
                                    value={formData.rate}
                                    onChange={(e) => {
                                        handleInputChange('rate', e.target.value);
                                        // Clear warning when user manually changes rate
                                        if (rateWarning) {
                                            setRateWarning(false);
                                        }
                                    }}
                                    placeholder="The subscription monthly rate"
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <ControlLabel>PO Number</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={formData.poNumber}
                                    onChange={(e) => handleInputChange('poNumber', e.target.value)}
                                    placeholder="Purchase order number"
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            {/* Empty column for layout balance */}
                        </Col>
                    </Row>

                    {suggestions.purchaserEmail && suggestions.purchaserId && (
                        <Row>
                            <Col md={12}>
                                <Alert bsStyle="info">
                                    <strong>Suggested Update:</strong> Found billing email "{suggestions.purchaserEmail}" in Stripe customer data. 
                                    This email exists in our system. Updating this order will assign the user as the purchaser unless you click below.
                                    <br />
                                    <Button 
                                        bsSize="sm" 
                                        bsStyle="danger" 
                                        style={{ marginTop: '10px' }}
                                        disabled={suggestions.purchaserId === null}
                                        onClick={() => {
                                            setSuggestions(prev => ({ ...prev, purchaserId: null }));
                                        }}
                                    >
                                        {suggestions.purchaserId === null ? 'Purchaser Removed' : 'Remove Suggested Purchaser'}
                                    </Button>
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {suggestions.suggestedDeliveryDate && (
                        <Row>
                            <Col md={12}>
                                <Alert bsStyle="info">
                                    <strong>Suggested Update:</strong> Found delivery date "{suggestions.suggestedDeliveryDate}" in Stripe customer metadata. 
                                    Updating this order will set the delivery date to this value unless you click below.
                                    <br />
                                    <Button 
                                        bsSize="sm" 
                                        bsStyle="danger" 
                                        style={{ marginTop: '10px' }}
                                        disabled={suggestions.suggestedDeliveryDate === null}
                                        onClick={() => setSuggestions(prev => ({ ...prev, suggestedDeliveryDate: null }))}
                                    >
                                        {suggestions.suggestedDeliveryDate === null ? 'Delivery Date Removed' : 'Remove Suggested Delivery Date'}
                                    </Button>
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {formData.subscriptionId && (
                        <Row>
                            <Col md={12}>
                                <Alert bsStyle="warning">
                                    <strong>Note:</strong> Assigning a subscription will update the order date to match the subscription creation date.
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {rateWarning && order && (
                        <Row>
                            <Col md={12}>
                                <Alert bsStyle="warning">
                                    <strong>Rate Change Warning:</strong> The order rate is being updated from {order.rate ? `$${order.rate}` : '$0'} to {formData.rate ? `$${(formData.rate / 100).toFixed(2)}` : '$0.00'} based on the selected subscription.
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {formData.status === 'DELETE' && (
                        <Row>
                            <Col md={12}>
                                <Alert bsStyle="danger">
                                    <strong>Delete Warning:</strong> Setting this order to DELETE status will hide it from the main orders list and exclude it from all statistics. This action can be reversed by changing the status back to any other value.
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    <FormGroup>
                        <ControlLabel>Notes</ControlLabel>
                        <FormControl
                            componentClass="textarea"
                            rows={3}
                            placeholder="Add any additional notes about this order..."
                            value={formData.notes}
                            onChange={(e) => handleInputChange('notes', e.target.value)}
                        />
                    </FormGroup>

                    <Row>
                        <Col md={12}>
                            <h4 style={{ fontFamily: 'inherit', fontSize: '16px', fontWeight: 'normal', marginBottom: '15px' }}>Order Information</h4>
                            <p><strong>Cap:</strong> {order.cap?.name || 'Loading...'}</p>
                            <p><strong>Builder:</strong> {order.builder?.name || 'Loading...'}</p>
                            <p><strong>Purchaser:</strong> {order.purchaser?.email || suggestions.purchaserEmail || 'Loading...'}</p>
                            <p><strong>Order Date:</strong> {order.addDate ? new Date(order.addDate).toLocaleDateString() : 'N/A'}</p>
                            <p><strong>PO Number:</strong> {order.poNumber || 'None'}</p>
                            <p><strong>Current Subscription:</strong> {order.subscriptionId || 'None'}</p>
                            <p><strong>Current Rate:</strong> {order?.rate ? `$${(order.rate / 100).toFixed(2)}` : '$0.00'}</p>
                        </Col>
                    </Row>
                </Modal.Body>
                
                <Modal.Footer>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        bsStyle="primary" 
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Glyphicon glyph="refresh" className="spinning" /> Updating...
                            </>
                        ) : (
                            'Update Order'
                        )}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
